/* nav-panels.js — Sneak Freak (AphCom) · mobile menu sliding panels
   Progressive enhancement over snippets/header-drawer.liquid. Every panel is already in the DOM and every
   link is reachable without this file; here it becomes a stack you push and pop.

   Contract: [data-nav-stack] wraps the panels. [data-nav-panel="root"] is the first one; a row carrying
   [data-nav-open="<id>"] pushes the panel with that id; [data-nav-back] pops. The drawer closing resets to
   root so it never reopens halfway down a category. */
'use strict';
(function () {
  if (window.__sfNavPanels) return;
  window.__sfNavPanels = true;

  const DUR = 260;

  function reduced() {
    if (window.theme && window.theme.reducedMotion) return true;
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function init(drawer) {
    const stack = drawer.querySelector('[data-nav-stack]');
    if (!stack || stack.dataset.navReady === 'true') return;
    stack.dataset.navReady = 'true';

    const panels = Array.from(stack.querySelectorAll('[data-nav-panel]'));
    const root = stack.querySelector('[data-nav-panel="root"]');
    /* Back now lives inside each panel (Figma puts it under the search row, not in the head), so there is
       one per panel and it is shown or hidden with its panel. A head-level Back, if a theme still has one, is
       still driven here. */
    const back = drawer.querySelector('.nav-drawer__head [data-nav-back]');
    if (!root) return;
    let trail = [root];
    let busy = false;

    const byId = (id) => stack.querySelector('[data-nav-panel="' + id + '"]');
    const current = () => trail[trail.length - 1];

    /* the drawer is a fixed-height column; each panel scrolls on its own so a long category never drags the
       root list with it */
    panels.forEach((p) => {
      p.classList.toggle('is-current', p === root);
      p.toggleAttribute('hidden', p !== root);
    });

    function syncBack() {
      if (!back) return;
      const deep = trail.length > 1;
      back.toggleAttribute('hidden', !deep);
      back.setAttribute('aria-label', deep ? (current().getAttribute('aria-label') || '') : '');
    }

    function move(from, to, forward) {
      if (!to || from === to || busy) return;
      busy = true;
      to.hidden = false;
      /* the incoming panel starts off to the side it is arriving from */
      to.classList.add(forward ? 'is-entering-fwd' : 'is-entering-back');
      /* one frame so the start position is committed before the transition */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          to.classList.add('is-current');
          to.classList.remove('is-entering-fwd', 'is-entering-back');
          from.classList.remove('is-current');
          from.classList.add(forward ? 'is-leaving-fwd' : 'is-leaving-back');
          const done = function () {
            from.classList.remove('is-leaving-fwd', 'is-leaving-back');
            from.hidden = true;
            busy = false;
            /* the panel that arrived owns the scroll position and the focus */
            const scroller = to.querySelector('.nav-panel__scroll') || to;
            scroller.scrollTop = 0;
            const target = to.querySelector('.nav-panel__title, .nav-row, .nav-sublink, a, button');
            if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
            syncBack();
          };
          if (reduced()) { done(); return; }
          let settled = false;
          const onEnd = function (e) {
            if (e && e.target !== from) return;
            if (settled) return;
            settled = true;
            from.removeEventListener('transitionend', onEnd);
            done();
          };
          from.addEventListener('transitionend', onEnd);
          /* a transition that never fires must not strand the menu mid-slide */
          setTimeout(onEnd, DUR + 220);
        });
      });
    }

    function push(id) {
      const to = byId(id);
      if (!to) return;
      const from = current();
      trail.push(to);
      move(from, to, true);
    }

    function pop() {
      if (trail.length < 2) return;
      const from = trail.pop();
      move(from, current(), false);
    }

    function reset() {
      trail.forEach(function (p) { p.classList.remove('is-current', 'is-leaving-fwd', 'is-leaving-back'); if (p !== root) p.hidden = true; });
      trail = [root];
      root.hidden = false;
      root.classList.add('is-current');
      const scroller = root.querySelector('.nav-panel__scroll') || root;
      scroller.scrollTop = 0;
      busy = false;
      syncBack();
    }

    drawer.addEventListener('click', function (e) {
      const opener = e.target.closest('[data-nav-open]');
      if (opener && drawer.contains(opener)) { e.preventDefault(); push(opener.getAttribute('data-nav-open')); return; }
      const backBtn = e.target.closest('[data-nav-back]');
      if (backBtn && drawer.contains(backBtn)) { e.preventDefault(); pop(); }
    });

    /* Escape steps back one panel before it closes the drawer. sf-drawer binds its own Escape on the same
       element and registers first, so a listener here would run too late and the drawer would close instead of
       stepping back. Capturing on the document runs ahead of it; from the root we do not intercept at all, so
       Escape still closes the drawer exactly as before. */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (drawer.hidden || trail.length < 2) return;
      if (!drawer.contains(e.target) && e.target !== drawer && e.target !== document.body) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      pop();
    }, true);

    /* The drawer is exactly as tall as what the shopper can see. A fixed inset: 0 box follows the layout
       viewport, which on a phone (Android's URL bar or bottom toolbar, the system navigation, the on-screen
       keyboard) can reach below the visible area and take the foot's button with it; the visual viewport is
       the visible area itself. Only while open, and not while pinch-zoomed, where the CSS dvh height stays. */
    const vv = window.visualViewport;
    function fit() {
      if (!vv || drawer.hidden) return;
      if (vv.scale > 1.01) {
        drawer.style.removeProperty('--nav-vh');
        drawer.style.removeProperty('--nav-vt');
        drawer.classList.remove('is-short');
        return;
      }
      drawer.style.setProperty('--nav-vh', Math.round(vv.height) + 'px');
      drawer.style.setProperty('--nav-vt', Math.round(vv.offsetTop) + 'px');
      drawer.classList.toggle('is-short', vv.height < 480);
    }
    if (vv) {
      drawer.addEventListener('drawer:open', fit);
      vv.addEventListener('resize', fit);
      vv.addEventListener('scroll', fit);
    }
    /* reopening always starts at the top level */
    drawer.addEventListener('sf-drawer:close', reset);
    const mo = new MutationObserver(function () { if (drawer.hidden) reset(); else fit(); });
    mo.observe(drawer, { attributes: true, attributeFilter: ['hidden'] });

    syncBack();
  }

  function arm() {
    document.querySelectorAll('sf-drawer[data-nav-stack], #nav-drawer').forEach(function (d) {
      if (d.querySelector('[data-nav-stack]')) init(d);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', arm, { once: true });
  else arm();
  /* the header section can be re-rendered by the theme editor */
  document.addEventListener('shopify:section:load', arm);
})();
